import { Component, OnInit, OnDestroy, HostListener, HostBinding, ElementRef, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Meta, Title } from '@angular/platform-browser';
import { ServicoRastreamentoBio } from '../services/servico-rastreamento-bio.service';

interface IndicadorHeatmap {
  top: number;
  left: number;
  width: number;
  height: number;
  cor: string;
  quantidade: number;
  percentual: number;
  borderRadius: number;
  eTopClique: boolean;
}

interface LinkBioPagina {
  id: number;
  tipo: string;
  titulo: string;
  subtitulo: string;
  icone: string;
  urlIconeCustomizado: string;
  url: string;
  etiqueta: string;
  ordem: number;
  ativo: boolean;
}

interface OfertaBioPagina {
  id: number;
  titulo: string;
  descricao: string;
  urlImagem: string;
  precoOriginal: number;
  precoPromocional: number;
  textoBadge: string;
  urlAcao: string;
  ordem: number;
  ativo: boolean;
}

interface BioPaginaPublica {
  id: number;
  slug: string;
  titulo: string;
  subtitulo: string;
  urlLogo: string;
  corPrimaria: string;
  corSecundaria: string;
  corFundo: string;
  corTexto: string;
  corFundoCard: string;
  corBotaoCta: string;
  borderRadius: number;
  fonte: string;
  textoBadgePromocional: string;
  exibirOfertas: boolean;
  links: LinkBioPagina[];
  ofertas: OfertaBioPagina[];
  empresa: any;
}

@Component({
  selector: 'app-bio-pagina',
  templateUrl: './bio-pagina.component.html',
  styleUrls: ['./bio-pagina.component.scss']
})
export class BioPaginaComponent implements OnInit, OnDestroy {

  bioPagina: BioPaginaPublica = null;
  carregando = true;
  erro = false;
  slug = '';

  // Carousel
  ofertaAtual = 0;
  totalOfertas = 0;
  touchStartX = 0;

  timerAutoPlay: any = null;

  // Horarios expandido
  horariosExpandido = false;

  // Responsive
  isMobile = true;

  // Heatmap
  modoHeatmap = false;
  dadosHeatmap: Map<string, {quantidade: number, percentual: number}> = new Map();
  maxQuantidadeHeatmap = 1;
  indicadoresHeatmap: IndicadorHeatmap[] = [];

  @HostBinding('style.backgroundColor')
  get hostBackground(): string {
    return this.bioPagina?.corFundo || '#FFF8F0';
  }

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private meta: Meta,
    private servicoTitulo: Title,
    private servicoRastreamentoBio: ServicoRastreamentoBio,
    private elRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.slug = this.route.snapshot.paramMap.get('slug') || '';
    this.modoHeatmap = this.route.snapshot.queryParamMap.get('heatmap') === 'true';
    this.isMobile = window.innerWidth <= 768;
    this.carregarBio();
    this.iniciarPostHog();
  }

  ngOnDestroy() {
    this.pararAutoPlay();
  }

  @HostListener('window:resize')
  onResize() {
    this.isMobile = window.innerWidth <= 768;
    this.calcularPosicoes();
  }

  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent) {
    if (!event.data) return;

    // Heatmap data from admin
    if (event.data.tipo === 'bio-heatmap-data' && this.modoHeatmap) {
      this.dadosHeatmap.clear();
      this.maxQuantidadeHeatmap = event.data.maxQuantidade || 1;
      const cliques: any[] = event.data.cliques || [];
      cliques.forEach(c => {
        const chave = c.tipoItem + '_' + c.idItem;
        this.dadosHeatmap.set(chave, { quantidade: c.quantidade, percentual: c.percentual });
      });
      setTimeout(() => this.calcularPosicoes());
      return;
    }

    // Preview update (existing)
    if (event.data.tipo !== 'bio-preview-update') return;
    if (!this.bioPagina) return;
    const dados = event.data;
    if (dados.corPrimaria != null) this.bioPagina.corPrimaria = dados.corPrimaria;
    if (dados.corSecundaria != null) this.bioPagina.corSecundaria = dados.corSecundaria;
    if (dados.corFundo != null) this.bioPagina.corFundo = dados.corFundo;
    if (dados.corTexto != null) this.bioPagina.corTexto = dados.corTexto;
    if (dados.corFundoCard != null) this.bioPagina.corFundoCard = dados.corFundoCard;
    if (dados.corBotaoCta != null) this.bioPagina.corBotaoCta = dados.corBotaoCta;
    if (dados.borderRadius != null) this.bioPagina.borderRadius = dados.borderRadius;
    if (dados.fonte != null) {
      this.bioPagina.fonte = dados.fonte;
      this.carregarFonteGoogle();
    }
  }

  carregarBio() {
    this.carregando = true;
    this.http.get('/api/bio/' + this.slug).toPromise().then((response: any) => {
      if (response && response.sucesso) {
        this.bioPagina = this.mapearBioPagina(response.data);
        this.totalOfertas = this.ofertasAtivas.length;
        this.iniciarAutoPlay();
        this.carregarFonteGoogle();
        this.configurarMetaTags();
        this.registrarVisualizacao();
        this.identificarPostHog();
      } else {
        this.erro = true;
      }
      this.carregando = false;
    }).catch(() => {
      this.erro = true;
      this.carregando = false;
    });
  }

  private mapearBioPagina(dados: any): BioPaginaPublica {
    return {
      id: dados.id,
      slug: dados.slug,
      titulo: dados.title,
      subtitulo: dados.subtitle,
      urlLogo: dados.logoUrl,
      corPrimaria: dados.primaryColor,
      corSecundaria: dados.secondaryColor,
      corFundo: dados.backgroundColor,
      corTexto: dados.textColor || '#333333',
      corFundoCard: dados.cardBackgroundColor || '#FFFFFF',
      corBotaoCta: dados.ctaButtonColor || null,
      borderRadius: dados.borderRadius != null ? dados.borderRadius : 12,
      fonte: dados.font || '',
      textoBadgePromocional: dados.promoBadgeText,
      exibirOfertas: dados.showOffers !== false,
      links: (dados.links || []).map((link: any) => ({
        id: link.id,
        tipo: link.type,
        titulo: link.title,
        subtitulo: link.subtitle,
        icone: link.icon,
        urlIconeCustomizado: link.customIconUrl,
        url: link.url,
        etiqueta: link.badge || '',
        ordem: link.sortOrder,
        ativo: link.isActive
      })),
      ofertas: (dados.offers || []).map((oferta: any) => ({
        id: oferta.id,
        titulo: oferta.title,
        descricao: oferta.description,
        urlImagem: oferta.imageUrl,
        precoOriginal: oferta.oldPrice,
        precoPromocional: oferta.newPrice,
        textoBadge: oferta.badgeText,
        urlAcao: oferta.actionUrl,
        ordem: oferta.sortOrder,
        ativo: oferta.isActive
      })),
      empresa: dados.empresa
    };
  }

  private carregarFonteGoogle() {
    if (!this.bioPagina?.fonte) return;
    const fonteEncoded = this.bioPagina.fonte.replace(/ /g, '+');
    const linkId = 'bio-google-font';
    if (!document.getElementById(linkId)) {
      const link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${fonteEncoded}:wght@400;600;700;800&display=swap`;
      document.head.appendChild(link);
    }
  }

  private configurarMetaTags() {
    if (!this.bioPagina) return;
    this.servicoTitulo.setTitle(this.bioPagina.titulo + ' | MeuCardapio Bio');

    this.meta.updateTag({ property: 'og:title', content: this.bioPagina.titulo });
    this.meta.updateTag({ property: 'og:description', content: this.bioPagina.subtitulo || '' });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:url', content: window.location.href });
    if (this.bioPagina.urlLogo) {
      this.meta.updateTag({ property: 'og:image', content: this.bioPagina.urlLogo });
    }
    this.meta.updateTag({ name: 'theme-color', content: this.bioPagina.corFundo || '#FFF8F0' });
  }

  private iniciarPostHog() {
    if (this.route.snapshot.queryParamMap.get('preview') === 'true' || this.modoHeatmap) return;
    const w = window as any;
    if (w.posthog && w.posthog.__SV) return;
    const snippet = document.createElement('script');
    snippet.textContent = `!function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once register_for_session unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group identify setPersonProperties setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags resetGroups onFeatureFlags addFeatureFlagsHandler onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);posthog.init('phc_Su5yrfxmlIaqLVl3NorVyCMll20alJAY165n7uPEBvE',{api_host:'https://us.i.posthog.com',defaults:'2026-01-30'})`;
    document.head.appendChild(snippet);
  }

  private identificarPostHog() {
    if (this.route.snapshot.queryParamMap.get('preview') === 'true' || this.modoHeatmap) return;
    const w = window as any;
    if (!w.posthog || !this.bioPagina) return;
    w.posthog.group('empresa', String(this.bioPagina.id), {
      nome: this.bioPagina.titulo,
      slug: this.bioPagina.slug
    });
  }

  private registrarVisualizacao() {
    if (this.route.snapshot.queryParamMap.get('preview') === 'true') return;
    this.servicoRastreamentoBio.registrarVisualizacaoPagina(this.slug);
  }

  registrarClique(tipo: string, itemId: number, url: string, titulo?: string, posicao?: number) {
    if (this.route.snapshot.queryParamMap.get('preview') === 'true') {
      if (url) {
        tipo === 'oferta' ? window.location.href = url : window.open(url, '_blank');
      }
      return;
    }
    this.servicoRastreamentoBio.registrarClique(this.slug, tipo, itemId, titulo, posicao);

    if (url) {
      tipo === 'oferta' ? window.location.href = url : window.open(url, '_blank');
    }
  }

  // Ofertas
  get ofertasAtivas(): any[] {
    if (!this.bioPagina?.ofertas) return [];
    return this.bioPagina.ofertas.filter((oferta: OfertaBioPagina) => oferta.ativo);
  }

  // Links
  get linksAtivos(): any[] {
    if (!this.bioPagina?.links) return [];
    return this.bioPagina.links.filter((link: LinkBioPagina) => link.ativo);
  }

  get linkCta(): any {
    return this.linksAtivos.length > 0 ? this.linksAtivos[0] : null;
  }

  get linksRegulares(): any[] {
    return this.linksAtivos.slice(1);
  }

  // Status da loja
  get statusLoja(): { aberto: boolean; texto: string } {
    if (!this.bioPagina?.empresa) return { aberto: false, texto: 'Fechado' };
    const aberto = this.bioPagina.empresa.estaRecebendoPedidos;
    return {
      aberto: aberto,
      texto: aberto ? 'Aberto' : 'Fechado'
    };
  }

  // Carousel controls
  proximaOferta() {
    if (this.ofertaAtual < this.totalOfertas - 1) {
      this.ofertaAtual++;
    }
  }

  ofertaAnterior() {
    if (this.ofertaAtual > 0) {
      this.ofertaAtual--;
    }
  }

  irParaOferta(index: number) {
    this.ofertaAtual = index;
    this.reiniciarAutoPlay();
    setTimeout(() => this.calcularPosicoes(), 350);
  }

  onTouchStart(event: TouchEvent) {
    this.touchStartX = event.touches[0].clientX;
  }

  onTouchEnd(event: TouchEvent) {
    const touchEndX = event.changedTouches[0].clientX;
    const diff = this.touchStartX - touchEndX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        this.proximaOferta();
      } else {
        this.ofertaAnterior();
      }
      this.reiniciarAutoPlay();
    }
  }

  iniciarAutoPlay() {
    this.pararAutoPlay();
    if (this.totalOfertas <= 1) return;
    this.timerAutoPlay = setInterval(() => {
      this.ofertaAtual = (this.ofertaAtual + 1) % this.totalOfertas;
      setTimeout(() => this.calcularPosicoes(), 350);
    }, 5000);
  }

  reiniciarAutoPlay() {
    if (this.timerAutoPlay) {
      this.pararAutoPlay();
      setTimeout(() => this.iniciarAutoPlay());
    }
  }

  pararAutoPlay() {
    if (this.timerAutoPlay) {
      clearInterval(this.timerAutoPlay);
      this.timerAutoPlay = null;
    }
  }

  toggleHorarios() {
    this.horariosExpandido = !this.horariosExpandido;
  }

  // Icone helpers
  obtenhaIconeDoTipo(tipo: string): string {
    const icones: any = {
      'cardapio': 'fas fa-utensils',
      'whatsapp': 'fab fa-whatsapp',
      'instagram': 'fab fa-instagram',
      'ifood': 'fas fa-motorcycle',
      '99food': 'fas fa-motorcycle',
      'aiqfome': 'fas fa-hamburger',
      'telefone': 'fas fa-phone-alt',
      'localizacao': 'fas fa-map-marker-alt',
      'reserva': 'fas fa-calendar-check',
      'site': 'fas fa-globe'
    };
    return icones[tipo] || 'fas fa-link';
  }

  obtenhaCorDoTipo(tipo: string): string {
    const cores: any = {
      'whatsapp': '#25D366',
      'instagram': '#E1306C',
      'ifood': '#EA1D2C',
      '99food': '#FFCC00',
      'aiqfome': '#7D0DC3',
      'telefone': '#1976D2',
      'localizacao': '#F57C00'
    };
    return cores[tipo] || null;
  }

  obtenhaHeatmap(tipo: string, id: number): {quantidade: number, percentual: number} {
    const chave = tipo + '_' + id;
    return this.dadosHeatmap.get(chave) || { quantidade: 0, percentual: 0 };
  }

  obtenhaCorHeatmap(tipo: string, id: number): string {
    const dados = this.obtenhaHeatmap(tipo, id);
    if (dados.quantidade === 0) return 'transparent';
    const intensidade = (dados.quantidade / this.maxQuantidadeHeatmap) * 100;

    if (intensidade <= 20) return 'rgba(255, 193, 7, 0.15)';
    if (intensidade <= 50) return 'rgba(255, 152, 0, 0.25)';
    if (intensidade <= 80) return 'rgba(255, 120, 0, 0.35)';
    return 'rgba(244, 67, 54, 0.40)';
  }

  calcularPosicoes() {
    if (!this.modoHeatmap || this.dadosHeatmap.size === 0) {
      this.indicadoresHeatmap = [];
      return;
    }
    const container = this.elRef.nativeElement.querySelector('.bio-page');
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const elementos = container.querySelectorAll('[data-heatmap]');
    const indicadores: IndicadorHeatmap[] = [];

    elementos.forEach((el: Element) => {
      const chave = el.getAttribute('data-heatmap');
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const [tipo, idStr] = chave.split('_');
      const id = parseInt(idStr, 10);
      const dados = this.obtenhaHeatmap(tipo, id);
      const computed = getComputedStyle(el);
      const br = parseFloat(computed.borderRadius) || 0;
      indicadores.push({
        top: rect.top - containerRect.top + container.scrollTop,
        left: rect.left - containerRect.left,
        width: rect.width,
        height: rect.height,
        cor: this.obtenhaCorHeatmap(tipo, id),
        quantidade: dados.quantidade,
        percentual: dados.percentual,
        borderRadius: br,
        eTopClique: false
      });
    });

    // Identificar o top clique
    const maxQuantidade = Math.max(...indicadores.map(i => i.quantidade));
    if (maxQuantidade > 0) {
      indicadores.forEach(i => {
        i.eTopClique = i.quantidade === maxQuantidade;
      });
    }

    this.indicadoresHeatmap = indicadores;

    this.cdr.detectChanges();
  }
}
